import type { BillingProfileInput } from '@noconflict/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../shared/api/supabase';
import { getBillingProfile, getBillingProfileComplete, saveBillingProfile } from './api';

export const billingProfileKeys = {
  org: (organizationId: string) => ['billing-profile', organizationId] as const,
  complete: (organizationId: string) => ['billing-profile', organizationId, 'complete'] as const,
};

export function useBillingProfile(organizationId: string, enabled = true) {
  return useQuery({
    queryKey: billingProfileKeys.org(organizationId),
    queryFn: () => getBillingProfile(supabase, organizationId),
    enabled,
  });
}

/** Yes or no, for anyone who can order; a save refetches it (same key prefix as the profile). */
export function useBillingProfileComplete(organizationId: string, enabled = true) {
  return useQuery({
    queryKey: billingProfileKeys.complete(organizationId),
    queryFn: () => getBillingProfileComplete(supabase, organizationId),
    enabled,
  });
}

/**
 * `onSettled`, because a 502 still means the profile was saved. The `order` prefix is refreshed too: whether an
 * order can be submitted depends on the profile being complete and on "reference required".
 */
export function useSaveBillingProfile(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BillingProfileInput) => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('forbidden');
      return saveBillingProfile(token, organizationId, input);
    },
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: billingProfileKeys.org(organizationId) }),
        qc.invalidateQueries({ queryKey: ['order'] }),
      ]),
  });
}
